import { cloneDeep, mapKeys, snakeCase } from 'lodash'

// A busca do Bubble leva segundos: a lista da página e o modal esperam pela mesma requisição.
let bubbleRequest = null

const defaultState = {
  current: null,
  timeZones: [],
  // null enquanto ninguém pediu a lista do Bubble.
  bubbleEvents: null,
  bubbleMeta: null
}

export const state = () => cloneDeep(defaultState)

export const getters = {
  current(state) {
    return state.current
  },
  timeZones(state) {
    return state.timeZones
  },
  bubbleEvents(state) {
    return state.bubbleEvents
  },
  bubbleMeta(state) {
    return state.bubbleMeta
  }
}

export const mutations = {
  current(state, current) {
    state.current = current
  },
  timeZones(state, timeZones) {
    state.timeZones = timeZones
  },
  bubbleImport(state, { data, meta }) {
    state.bubbleEvents = data
    state.bubbleMeta = meta
  }
}

export const actions = {
  // O interceptor do coreApi desserializa o JSON:API mas mantém o envelope { data }.
  async create(
    _,
    {
      name,
      federationId,
      dateStart,
      dateEnd,
      venueId,
      championships,
      bubbleEventId,
      weightUnit,
      timeZoneIana
    }
  ) {
    const response = await this.$coreApi.$post('staff/events', {
      name,
      federation_id: federationId,
      date_start: dateStart,
      date_end: dateEnd,
      venue_id: venueId,
      championships: championships.map(
        ({ typeId, kimonoType, name, abbr, registrationsLimit, dates }) => ({
          type_id: typeId,
          kimono_type: kimonoType,
          ...(name && { name }),
          ...(abbr && { abbr }),
          ...(registrationsLimit != null && { registrations_limit: registrationsLimit }),
          ...(dates && { dates: mapKeys(dates, (_day, key) => snakeCase(key)) })
        })
      ),
      ...(bubbleEventId && { bubble_event_id: bubbleEventId }),
      ...(weightUnit && { weight_unit: weightUnit }),
      ...(timeZoneIana && { time_zone_iana: timeZoneIana })
    })

    return response.data
  },

  async update({ commit }, { id, ...payload }) {
    const response = await this.$coreApi.$put(`staff/events/${id}`, payload)

    commit('current', response.data)

    return response.data
  },

  // O core guarda a carga do Bubble alguns minutos; `refresh` pede para ele buscar de novo.
  async fetchBubbleEvents({ commit }, { refresh = false } = {}) {
    if (bubbleRequest && !refresh) return bubbleRequest

    const request = this.$coreApi
      .$get('staff/bubble_events', { params: refresh ? { refresh: true } : {} })
      .then(response => {
        commit('bubbleImport', response)

        return response
      })

    bubbleRequest = request

    try {
      return await request
    } finally {
      if (bubbleRequest === request) bubbleRequest = null
    }
  },

  // O catálogo não muda durante a sessão: uma leitura basta.
  async getTimeZones({ commit, state }) {
    if (state.timeZones.length) return state.timeZones

    const response = await this.$coreApi.$get('staff/events/time_zones')

    commit('timeZones', response.data)

    return response.data
  },

  // O core devolve o evento inteiro depois de mexer na composição, então a tela remonta o
  // formulário a partir da resposta em vez de pedir o evento de novo.
  async addChampionship({ commit }, { id, typeId, kimonoType }) {
    const response = await this.$coreApi.$post(`staff/events/${id}/championships`, {
      type_id: typeId,
      kimono_type: kimonoType
    })

    commit('current', response.data)

    return response.data
  },

  async removeChampionship({ commit }, { id, championshipId }) {
    const response = await this.$coreApi.$delete(
      `staff/events/${id}/championships/${championshipId}`
    )

    commit('current', response.data)

    return response.data
  },

  async remove(_, id) {
    await this.$coreApi.$delete(`staff/events/${id}`)
  },

  async getById({ commit }, id) {
    try {
      const response = await this.$coreApi.$get(`staff/events/${id}`)

      commit('current', response.data)

      return response.data
    } catch (error) {
      // Sem isso o hub seguinte abriria com o evento que o staff tinha aberto antes.
      commit('current', null)

      throw error
    }
  },

  // Releitura do evento já aberto: se o core falhar, fica o que a tela já mostrava.
  async refresh({ commit }, id) {
    const response = await this.$coreApi.$get(`staff/events/${id}`)

    commit('current', response.data)
  }
}
